/**
 * OMON-137 admin hamburger menu shell.
 *
 * Owns the hamburger button + dropdown lifecycle:
 *   1. After claims load (auth.ts → state.authClaims), `applyClaimsToShell`
 *      decides whether the hamburger button is visible and renders the
 *      dropdown's items according to ADR-0013 §5's role permissions.
 *   2. Click on the hamburger toggles the dropdown.
 *   3. Click on a menu item switches the visible panel via
 *      `switchToAdminPanel` (mirrors navigation.ts's switchToTab).
 *   4. Click outside the menu closes it.
 *
 * The dropdown items are computed once per claims-load — there's no
 * per-render churn. State change only happens on sign-in / sign-out
 * boundaries.
 */
import {
  canSeeAdminMenu,
  canSeeAuditLog,
  canSeeConnect,
  canSeeSomaAdoption,
  canSeeGoalsInventoryTab,
  canSeeSOMAPlansTab,
  canSeeUsersAndRoles,
  canSeeAccessRequests,
  defaultPanelFor,
  showsTestModeIndicator,
  type AuthClaims,
} from "./claims";
import { state } from "../state";
import { adminMenu } from "../dom";
import { renderAdminUsersRoles } from "./users-roles";
import { renderAdminAuditLog } from "./audit-log";
import { renderAdminAccessRequests } from "./access-requests";
import { go } from "../router";
import { refreshAccountMenu } from "../account-menu";
import { renderViewAccess } from "./view-access-view";

export type AdminPanel = "users-roles" | "audit-log" | "access-requests";

interface MenuItem {
  panel: AdminPanel;
  /** Full label (panel heading / tooltip). */
  label: string;
  /** Short rail-icon label (the rail is ~80px wide). */
  railLabel: string;
  description: string;
}

// OMON-186: inline SVG icons for the admin rail items (the web app has no icon
// library). currentColor so they grey/maroon with the shared rail-item states.
const ADMIN_ICONS: Record<AdminPanel, string> = {
  "users-roles":
    '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="9" cy="8" r="3"/><path d="M3.5 20a5.5 5.5 0 0 1 11 0"/><circle cx="17" cy="9" r="2.3"/><path d="M15.5 20a4.5 4.5 0 0 1 6-4"/></svg>',
  "audit-log":
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 3h9l3 3v15H6z"/><path d="M14 3v4h4"/><path d="M9 12h6M9 16h6"/></svg>',
  "access-requests":
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 6h16v12H4z"/><path d="M4 7l8 6 8-6"/><circle cx="18.5" cy="6.5" r="3" fill="currentColor" stroke="none"/></svg>',
};

/**
 * Compute the visible menu items for a given claim. Centralised so the
 * permission table from ADR-0013 §5 lives in exactly one place
 * (claims.ts predicates) and the labels live in exactly one place
 * (this function).
 */
function visibleMenuItems(claims: AuthClaims | null): MenuItem[] {
  const items: MenuItem[] = [];
  if (canSeeUsersAndRoles(claims)) {
    items.push({
      panel: "users-roles",
      label: "Users & Roles",
      railLabel: "Users",
      description: "Manage _practitionerRole assignments",
    });
  }
  if (canSeeAccessRequests(claims)) {
    items.push({
      panel: "access-requests",
      label: "Access requests",
      railLabel: "Access",
      description: "Family app access awaiting the office",
    });
  }
  if (canSeeAuditLog(claims)) {
    items.push({
      panel: "audit-log",
      label: "Audit Log",
      railLabel: "Audit",
      description: "Chronological _auditEvent feed",
    });
  }
  return items;
}

/**
 * Update the hamburger visibility and menu contents based on the
 * current `state.authClaims`. Called by auth.ts after claims load,
 * and again on sign-out (with claims=null) to tear the menu down.
 *
 * OMON-027: also hides/shows the top-level nav tabs (SOMA Plans /
 * Goals Inventory) based on `canSeeSOMAPlansTab` / `canSeeGoalsInventoryTab`
 * predicates, and renders the TEST MODE banner when
 * `showsTestModeIndicator(claims)` is true.
 */
export function applyClaimsToShell(): void {
  const claims = state.authClaims;
  const items = visibleMenuItems(claims);

  // OMON-186: admin items render as rail icons (no hamburger). The whole admin
  // group shows only when claims permit (ADR-0013 §5) — same predicates as
  // before, so a care manager still sees no admin icons. Re-rendering wholesale
  // on each claims load is fine: this fires at sign-in/out, not per-keystroke.
  adminMenu.innerHTML = "";
  if (canSeeAdminMenu(claims) && items.length > 0) {
    adminMenu.style.display = "";
    for (const item of items) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "admin-menu-item";
      btn.dataset.panel = item.panel;
      btn.title = item.description;
      btn.innerHTML = `${ADMIN_ICONS[item.panel]}<span class="rail-lbl">${item.railLabel}</span>`;
      btn.addEventListener("click", () => {
        // OMON-202: navigate via the URL so admin panels are back-able too.
        go({ kind: "admin", panel: item.panel });
      });
      adminMenu.appendChild(btn);
    }
  } else {
    // Collapse the group so its divider/spacing doesn't leave a gap.
    adminMenu.style.display = "none";
  }

  // OMON-027: top-level nav tab visibility per ADR-0013 §3.
  applyNavTabVisibility(claims);
  // OMON-027: TEST MODE banner when dataScope === "test".
  applyTestModeBanner(claims);
  // The account menu's header follows the signed-in person (Ed, 2026-09-24).
  refreshAccountMenu(claims);
  // Who can see what: computed from the same predicates used above.
  if (canSeeUsersAndRoles(claims)) renderViewAccess();
}

/**
 * OMON-027: show/hide the SOMA Plans and Goals Inventory nav tabs based
 * on the user's role.
 *
 * If the currently-active tab becomes hidden (e.g. claims load shows a
 * planner is viewing Goals Inventory — only really possible if the
 * panel was activated programmatically before claims arrived), this
 * function flips to the first visible tab via `defaultPanelFor`. If
 * no tabs are visible (carer signing in, unknown role, nil claims),
 * the no-access empty state is rendered.
 */
function applyNavTabVisibility(claims: AuthClaims | null): void {
  const tabClients = document.querySelector<HTMLElement>('.nav-tab[data-panel="clients"]');
  const tabCatalogue = document.querySelector<HTMLElement>('.nav-tab[data-panel="catalogue"]');
  const tabConnect = document.querySelector<HTMLElement>('.nav-tab[data-panel="connect"]');
  const panelClients = document.getElementById("panel-clients");
  const panelCatalogue = document.getElementById("panel-catalogue");
  const panelConnect = document.getElementById("panel-connect");

  const seePlans = canSeeSOMAPlansTab(claims);
  const seeCatalogue = canSeeGoalsInventoryTab(claims);
  // BOFF-029 slice 1: Connect is an admin-only preview for now.
  const seeConnect = canSeeConnect(claims);

  // Hide the tab BUTTON only — leave the panel itself accessible so
  // the empty-state DOM can live in the same container.
  if (tabClients) tabClients.style.display = seePlans ? "" : "none";
  if (tabCatalogue) tabCatalogue.style.display = seeCatalogue ? "" : "none";
  if (tabConnect) tabConnect.style.display = seeConnect ? "" : "none";
  // MONO-111 phase 2b: the chase queue shares Connect's population.
  const tabChase = document.querySelector<HTMLElement>('[data-panel="chase"]');
  if (tabChase) tabChase.style.display = seeConnect ? "" : "none";
  // Soma planner adoption: admin-only (it names planners).
  const seeAdoption = canSeeSomaAdoption(claims);
  const tabAdoption = document.querySelector<HTMLElement>('[data-panel="adoption"]');
  if (tabAdoption) tabAdoption.style.display = seeAdoption ? "" : "none";

  // Render no-access empty state when no tabs are visible.
  const noAccess = !seePlans && !seeCatalogue;
  renderNoAccessState(noAccess, claims);

  // If the current active panel is one the user cannot see, swap to
  // the default. Read the active panel from the DOM rather than state —
  // navigation.ts owns `panelName` locally and doesn't expose it.
  const activePanel = document.querySelector<HTMLElement>(".panel.active")?.id;
  let mustSwap = false;
  if (activePanel === "panel-clients" && !seePlans) mustSwap = true;
  if (activePanel === "panel-catalogue" && !seeCatalogue) mustSwap = true;
  if (activePanel === "panel-connect" && !seeConnect) mustSwap = true;
  if (activePanel === "panel-chase" && !seeConnect) mustSwap = true;
  if (activePanel === "panel-adoption" && !seeAdoption) mustSwap = true;

  if (mustSwap || noAccess) {
    // Clear all panel-active states; the no-access state takes over
    // visually. If a permitted panel exists, render it instead.
    panelClients?.classList.remove("active");
    panelCatalogue?.classList.remove("active");
    panelConnect?.classList.remove("active");
    document.querySelectorAll(".nav-tab").forEach((t) => t.classList.remove("active"));

    const defaultPanel = defaultPanelFor(claims);
    if (defaultPanel === "clients" && panelClients) {
      panelClients.classList.add("active");
      tabClients?.classList.add("active");
    } else if (defaultPanel === "catalogue" && panelCatalogue) {
      panelCatalogue.classList.add("active");
      tabCatalogue?.classList.add("active");
    }
  }
}

/**
 * OMON-027: Renders or removes the no-access empty state. Sits in a
 * dedicated `#noAccessState` container so it can coexist with the
 * panel containers in the same DOM region.
 */
function renderNoAccessState(visible: boolean, claims: AuthClaims | null): void {
  let container = document.getElementById("noAccessState");
  if (!visible) {
    container?.remove();
    return;
  }
  if (!container) {
    container = document.createElement("div");
    container.id = "noAccessState";
    container.className = "no-access-state";
    container.setAttribute("role", "region");
    container.setAttribute("aria-label", "No access");
    const appScreen = document.getElementById("appScreen") || document.body;
    appScreen.appendChild(container);
  }
  const roleLabel = claims?.role ?? "no role assigned";
  container.innerHTML = `
    <div class="no-access-card">
      <h2>No access</h2>
      <p>Your account (<code>${escapeHtml(roleLabel)}</code>) has no surfaces in the web planner.</p>
      <p>If you're a carer, use the iOS app to record observations. Otherwise, contact your administrator.</p>
    </div>
  `;
}

/**
 * OMON-027: TEST MODE banner — mirrors the iOS Phase 2 banner. Visible
 * only when `dataScope === "test"`. `both` (Ed in dev) intentionally
 * hides the indicator because context is chosen by which build/origin
 * he opens.
 */
function applyTestModeBanner(claims: AuthClaims | null): void {
  let banner = document.getElementById("testModeBanner");
  if (!showsTestModeIndicator(claims)) {
    banner?.remove();
    return;
  }
  if (!banner) {
    banner = document.createElement("div");
    banner.id = "testModeBanner";
    banner.className = "test-mode-banner";
    banner.setAttribute("role", "status");
    banner.setAttribute("aria-label", "Test mode active");
    banner.textContent = "🧪 TEST MODE — fictional data only";
    // Insert at the top of appScreen so it sits above the nav tabs.
    const appScreen = document.getElementById("appScreen");
    if (appScreen) {
      appScreen.insertBefore(banner, appScreen.firstChild);
    }
  }
}

/**
 * Tiny HTML-escape helper for the no-access label. Centralised here
 * because the rest of the web app uses textContent rather than
 * innerHTML — this function is the one local exception.
 */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Swap the visible panel to one of the admin sub-screens. Deactivates
 * any nav-tab so the SOMA Plans / Goals Inventory active styling
 * clears (admin panels don't have a corresponding nav-tab). Calls the
 * per-panel renderer to populate content.
 */
export async function switchToAdminPanel(panel: AdminPanel): Promise<void> {
  document.querySelectorAll(".nav-tab").forEach((t) => t.classList.remove("active"));
  document.querySelectorAll(".panel").forEach((p) => p.classList.remove("active"));
  const panelId =
    panel === "users-roles"
      ? "panel-admin-users-roles"
      : panel === "access-requests"
        ? "panel-admin-access-requests"
        : "panel-admin-audit-log";
  document.getElementById(panelId)?.classList.add("active");

  // Reflect the active item in the rail's admin group.
  adminMenu.querySelectorAll(".admin-menu-item").forEach((btn) => {
    btn.classList.toggle(
      "active",
      (btn as HTMLButtonElement).dataset.panel === panel,
    );
  });

  state.adminPanel = panel;

  if (panel === "users-roles") {
    await renderAdminUsersRoles();
  } else if (panel === "access-requests") {
    await renderAdminAccessRequests();
  } else {
    await renderAdminAuditLog();
  }
}

/**
 * OMON-186: the admin surface is now a claims-gated icon group rendered inline
 * in the left rail by `applyClaimsToShell` (each button binds its own click to
 * `switchToAdminPanel`). There's no hamburger to toggle or dropdown to dismiss,
 * so this is a no-op — kept (and still called from main.ts) so the module's
 * init contract is unchanged and a future shell-wide binding has a home.
 */
export function bindAdminShell(): void {
  /* no-op — see note above */
}
